import React, { useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { type ButtonProps } from '@atlaskit/button/new';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { TeamInlinePreview } from '@atlassian/team-inline-preview';

import messages from '../archive-teams-card/messages';
import type { AtlassianTeamUpdateArchiveHydratedAction } from '../types';
import { formatTeamMembersPreviewCount } from '../utils/team-members-preview';

import { TeamMembersAvatarGroup } from './team-members-avatar-group';

type TeamCardActionProps = ButtonProps & {
	onClick: (team: AtlassianTeamUpdateArchiveHydratedAction) => Promise<void> | void;
};

type TeamCardProps = {
	team: AtlassianTeamUpdateArchiveHydratedAction;
	actions: TeamCardActionProps[];
	cloudId: string;
};

export const TeamCard = ({ team, actions }: TeamCardProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [loadingActionIdx, setLoadingActionIdx] = useState<number | null>(null);
	const members = team.data.members?.edges?.map((e) => e?.node?.member).filter(Boolean) ?? [];
	const memberCount = formatTeamMembersPreviewCount(members.length);
	const teamTypeName = team.data.type?.name;

	const getDescription = () => {
		const hasMembersData = team.data.members?.edges != null;

		if (teamTypeName && hasMembersData) {
			return formatMessage(messages.teamDescription, {
				teamType: teamTypeName,
				memberCount,
			});
		}
		if (teamTypeName) {
			return teamTypeName;
		}
		if (hasMembersData) {
			return formatMessage(messages.teamDescriptionMembersOnly, {
				memberCount,
			});
		}
		return undefined;
	};

	return (
		<Stack space="space.150">
			<Stack space="space.100">
				<TeamInlinePreview
					teamId={team.data.teamId}
					teamName={team.data.displayName ?? ''}
					description={getDescription()}
					overrideAvatarUrl={team.data.largeAvatarImageUrl ?? undefined}
					isVerified={team.data.isVerified ?? undefined}
					singleLine
				/>
				<TeamMembersAvatarGroup
					members={members.map((m) => ({
						accountId: m?.accountId ?? '',
						name: m?.name ?? '',
						picture: m?.picture ?? undefined,
					}))}
				/>
				<Text maxLines={3}>{team.data.description}</Text>
			</Stack>
			<Inline space="space.075">
				{actions.map((action, idx) => (
					<Button
						{...action}
						key={idx}
						spacing="compact"
						onClick={async () => {
							setLoadingActionIdx(idx);
							await action.onClick(team);
							setLoadingActionIdx(null);
						}}
						isLoading={loadingActionIdx === idx}
					>
						{action.children}
					</Button>
				))}
			</Inline>
		</Stack>
	);
};
